/* eslint-disable max-len */
import { Dispatch, SetStateAction, useState } from "react";
import { StaticDatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import { useTranslate } from "react-admin";
import { CustomButton } from "./CustomButton";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const viewButtonStyle =
  "h-[32px] flex justify-center px-[16px] border-none items-center bg-mono-primaryLightBgColor font-['Inter'] font-[400] text-[14px] text-mono-primaryTextColor";
const activeViewButtonStyle =
  "h-[32px] flex justify-center px-[16px] border-none items-center bg-[#F8D1C0] font-['Inter'] font-[400] text-[14px] text-mono-black";

interface CalendarDatesSelectProps {
  calendarApi: any;
  handleDatePopoverClose: () => void;
  selectedView?: string;
  setSelectedView?: Dispatch<SetStateAction<string>>;
  setCalendarsDate?: (date: Dayjs | null) => void;
  localStorageKey?: string;
  timezone?: string;
}

const CalendarDatesSelect = ({
  calendarApi,
  handleDatePopoverClose,
  setSelectedView,
  setCalendarsDate,
  localStorageKey,
  timezone
}: CalendarDatesSelectProps) => {
  const translate = useTranslate();
  const dateFilterFromLocalStorage = localStorageKey
    ? localStorage.getItem(localStorageKey)
    : null;
  const parsedDateFilter =
    dateFilterFromLocalStorage !== null && dateFilterFromLocalStorage !== "null"
      ? dayjs(JSON.parse(dateFilterFromLocalStorage))
      : dayjs();
  const [value, setValue] = useState<Dayjs | null>(parsedDateFilter);
  const [activeView, setActiveView] = useState<string>(
    calendarApi?.currentDataManager?.state?.currentViewType
  );

  return (
    <div className="bg-mono-primaryLightBgColor text-[22px] font-['OptimaLTStd'] font-[500] text-mono-primaryTextColor">
      <div className="flex flex-wrap sm:flex-nowrap">
        <div>
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            openTo="day"
            value={value}
            onChange={(newValue) => {
              setValue(newValue);
            }}
            sx={{
              backgroundColor: "#FAF9F7",
              "& .Mui-selected": {
                backgroundColor: "#F8D1C0 !important",
                color: "#000000 !important"
              },
              "& .MuiPickersDay-root:hover": {
                backgroundColor: "#F6F4F1"
              }
            }}
          />
        </div>
        <div className="flex flex-col w-full border-l border-[rgba(48,47,46,0.1)]">
          <button
            className={
              activeView === "timeGridDay"
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => setActiveView("timeGridDay")}
          >
            {translate("common.day_view")}
          </button>
          <button
            className={
              activeView === "timeGridWeek"
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => setActiveView("timeGridWeek")}
          >
            {translate("common.week_view")}
          </button>
          <button
            className={
              activeView === "dayGridMonth"
                ? activeViewButtonStyle
                : viewButtonStyle
            }
            onClick={() => setActiveView("dayGridMonth")}
          >
            {translate("common.month_view")}
          </button>
        </div>
      </div>
      <div className="flex w-full justify-end items-center gap-[8px] border-t border-[rgba(48,47,46,0.1)] p-[12px]">
        <CustomButton
          text={translate("common.cancel")}
          variant="tertiary"
          onClick={() => {
            handleDatePopoverClose();
          }}
        />
        <CustomButton
          text={translate("common.done")}
          onClick={() => {
            if (setCalendarsDate && value) {
              setCalendarsDate(value);
              if (localStorageKey) {
                localStorage.setItem(
                  localStorageKey,
                  JSON.stringify(dayjs(value.format()))
                );
              }
            }
            calendarApi?.changeView(activeView, dayjs(value).format());
            if (setSelectedView) {
              setSelectedView(
                activeView === "timeGridDay" ? "day" : activeView
              );
            }
            handleDatePopoverClose();
          }}
        />
      </div>
    </div>
  );
};

export default CalendarDatesSelect;
